import { BrLoading, BrRadioGroup, BrSelect, BrTextarea } from '@govbr-ds/react-components';
import { useQuery } from '@tanstack/react-query';
import AnmSelectQuery from 'components/anmSelectQuery/anmSelectQuery';
import ErrorMessage from 'components/errorMessage/errorMessage';
import { ArrecadacaoContext } from 'contexts/ArrecadacaoContext';
import { getValueFromETipoRecolhimentoByIndex } from 'enums/ETipoRecolhimento';
import { IErrorWithSentryId } from 'interfaces/IErrorWithSentryId';
import { INovaGuiaRecolhimento, IReceberDadosGuiaRecolhimento } from 'interfaces/IGuiaRecolhimento';
import { IMunicipio } from 'interfaces/IMunicipio';
import { IProcessoMinerario, IProcessoMinerarioETipoVinculo } from 'interfaces/IProcessoMinerario';
import { ISubstancia } from 'interfaces/ISubstancia';
import { useBackEndRequests } from 'pages/TitularArrendatario/lib/backEndRequests';
import React, { useContext, useEffect, useState } from 'react';
import { Button, Col, Modal, Row } from 'react-dsgov';
import { useNavigate } from 'react-router-dom';
import { Tooltip } from 'react-tooltip';
import { formatarNrProcesso } from 'utils/formatNrProcesso';
import { listaAnos, listaMeses } from 'utils/listaMesesAnos';
import { mesParaAbreviacao } from 'utils/mesParaAbreviacao';
import styles from './dadosRecolhimento.module.scss';

interface IDadosRecolhimentoProps {
  isViewOnly: boolean;
  dadosRecolhimento: INovaGuiaRecolhimento;
  setDadosRecolhimento: React.Dispatch<React.SetStateAction<INovaGuiaRecolhimento>>;
  isEdicaoDadosRecolhimento: boolean;
  setIsEdicaoDadosRecolhimento: React.Dispatch<React.SetStateAction<boolean>>;
  stepAtual: number;
  isPrimeiroAdquirente?: boolean;
}

const DadosRecolhimento = ({
  isViewOnly,
  dadosRecolhimento,
  setDadosRecolhimento,
  isEdicaoDadosRecolhimento,
  setIsEdicaoDadosRecolhimento,
  stepAtual,
}: IDadosRecolhimentoProps) => {
  const [mes, setMes] = useState<number | undefined>(undefined);
  const [ano, setAno] = useState<number | undefined>(undefined);
  const [processo, setProcesso] = useState<string>('');
  const [municipio, setMunicipio] = useState<number>(-1);
  const [substancia, setSubstancia] = useState<string>('');
  const [tipoVinculo, setTipoVinculo] = useState<number>(-1);
  const [observacoes, setObservacoes] = useState<string>('');
  const [modalText, setModalText] = useState<string>('');
  const [modalTitle, setModalTitle] = useState<string>('');
  const [isModalOpened, setIsModalOpened] = useState<boolean>(false);
  const [listaProcessosData, setListaProcessosData] = useState<IProcessoMinerarioETipoVinculo[]>([
    { nrProcesso: '', tipoRelacao: -1 },
  ]);

  const urlParams = new URLSearchParams(window.location.search);
  const guiaRecolhimentoId = urlParams.get('id') || '';

  const userCpfCnpj = sessionStorage.getItem('userCpfCnpj');
  const context = useContext(ArrecadacaoContext);
  const { getListaProcessoPorCpfCnpj, getProcessoMinerarioPorNumero, getDadosRecolhimento } = useBackEndRequests();
  const navigate = useNavigate();

  const {
    data: dataDadosRecolhimento,
    isLoading: isLoadingDadosRecolhimento,
    error: errorDadosRecolhimento,
    refetch: refetchDadosRecolhimento,
  } = useQuery<IReceberDadosGuiaRecolhimento, IErrorWithSentryId>({
    queryKey: ['dadosRecolhimentoQuery', guiaRecolhimentoId],
    queryFn: () => getDadosRecolhimento(guiaRecolhimentoId),
    refetchOnWindowFocus: false,
    retryDelay: 100,
  });

  useEffect(() => {
    refetchDadosRecolhimento();
  }, [guiaRecolhimentoId, stepAtual]);

  useEffect(() => {
    if (dataDadosRecolhimento) {
      setIsEdicaoDadosRecolhimento(true);
      setMes(dataDadosRecolhimento.competenciaMes);
      setAno(dataDadosRecolhimento.competenciaAno);
      setProcesso(dataDadosRecolhimento.numeroProcesso);
      setMunicipio(dataDadosRecolhimento.municipio.codigoIBGE);
      setSubstancia(dataDadosRecolhimento.substancia);
      setTipoVinculo(dataDadosRecolhimento.tipoRelacao);
      setObservacoes(dataDadosRecolhimento.observacoes);
      context?.setNumeroProcesso(dataDadosRecolhimento.numeroProcesso);
      context?.setSubstancia(dataDadosRecolhimento.substancia);
      context?.setMunicipio(dataDadosRecolhimento.municipio);
      context?.setCompetenciaAno(dataDadosRecolhimento.competenciaAno);
      context?.setCompetenciaMes(dataDadosRecolhimento.competenciaMes);
    } else if (errorDadosRecolhimento?.status === 404) {
      setIsEdicaoDadosRecolhimento(false);
    }
  }, [dataDadosRecolhimento, errorDadosRecolhimento]);

  useEffect(() => {
    setDadosRecolhimento({
      ...dadosRecolhimento,
      id: guiaRecolhimentoId,
      cpfCnpjDeclarante: userCpfCnpj || '',
      competenciaMes: Number(mes) || 0,
      competenciaAno: Number(ano) || 0,
      numeroProcesso: processo,
      municipio: Number(municipio),
      substancia: substancia,
      observacoes: observacoes,
      tipoRelacao: tipoVinculo,
    });
  }, [mes, ano, processo, municipio, substancia, observacoes, tipoVinculo]);

  useEffect(() => {
    if (!isEdicaoDadosRecolhimento && !isViewOnly) {
      if (processo != '') {
        const processoSelecionado = listaProcessosData.find((p) => p.nrProcesso === processo);
        setTipoVinculo(processoSelecionado?.tipoRelacao || -1);
      }
    }
  }, [processo]);

  const viewOnlyContent = (label: string, content: string | number, isDadosRecolhimento: boolean = false) => (
    <>
      <Tooltip
        opacity={1}
        id='td-tipo-recolhimento'
        place='bottom'
        className={styles.tooltip}
        variant='warning'
        disableTooltip={() => content === 0 || content === 1}
      >
        <div className={styles.tooltipText}>
          O tipo de recolhimento só é definido entre Original e Complementar após o pagamento da guia.
        </div>
      </Tooltip>

      <div
        className={styles.viewOnlyContent}
        data-tooltip-id={isDadosRecolhimento ? 'td-tipo-recolhimento' : undefined}
      >
        <span className={styles.label}>{label}</span>
        <span className={styles.content}>{content || '-'}</span>
      </div>
    </>
  );

  useEffect(() => {
    if (!isEdicaoDadosRecolhimento && !isViewOnly) {
      setSubstancia('');
      setMunicipio(-1);
    }
  }, [processo]);

  useEffect(() => {
    if (!isEdicaoDadosRecolhimento && !isViewOnly) {
      setProcesso('');
      setSubstancia('');
      setMunicipio(-1);
    }
    if (Number(ano) === 2017 && mes !== undefined && mes < 11) {
      setModalTitle('Competência Inválida');
      setModalText(
        'Para o ano de 2017, apenas os meses a partir de Novembro são válidos. Por favor, selecione uma competência válida.'
      );
      setIsModalOpened(true);
      setMes(undefined);
    }

    if (Number(ano) === new Date().getFullYear() && mes !== undefined && mes == new Date().getMonth() + 1) {
      setModalTitle('Competência Inválida');
      setModalText('Não é possível selecionar o mês corrente. Por favor, selecione uma competência válida.');
      setIsModalOpened(true);
      setMes(undefined);
    }
    if (Number(ano) === new Date().getFullYear() && mes !== undefined && mes > new Date().getMonth() + 1) {
      setModalTitle('Competência Inválida');
      setModalText('Não é possível selecionar uma competência futura. Por favor, selecione uma competência válida.');
      setIsModalOpened(true);
      setMes(undefined);
    }
  }, [ano, mes]);

  return (
    <div className={isViewOnly ? styles.containerViewOnly : styles.container}>
      {!isLoadingDadosRecolhimento && !!errorDadosRecolhimento && errorDadosRecolhimento?.status !== 404 && (
        <Modal modalOpened useScrim className={styles.errorModal}>
          <ErrorMessage
            errorMessage={errorDadosRecolhimento?.message || ''}
            errorTitle={'Erro ao carregar dados do recolhimento'}
            errorDescription={
              (errorDadosRecolhimento?.response?.data as { message?: string })?.message ||
              'Não foi possivel carregar os dados do recolhimento. Por favor, tente novamente mais tarde.'
            }
            onClickFunction={() => navigate('/')}
            buttonMessage={'Voltar para a página inicial'}
            noBorder
            imageWidth={280}
          />
        </Modal>
      )}

      {isLoadingDadosRecolhimento && <BrLoading large label='Carregando' />}
      {!isLoadingDadosRecolhimento && (!errorDadosRecolhimento || errorDadosRecolhimento?.status === 404) && (
        <>
          <Modal title={modalTitle} useScrim modalOpened={isModalOpened}>
            <Modal.Body>{modalText}</Modal.Body>
            <Modal.Footer justify-content='end'>
              <Button primary small m={2} onClick={() => setIsModalOpened(false)}>
                Confirmar
              </Button>
            </Modal.Footer>
          </Modal>
          <Row>
            <Col data-tooltip-id='select-competencia-tooltip'>
              {isViewOnly || isEdicaoDadosRecolhimento ? (
                viewOnlyContent('Competência', listaMeses.find((m) => m.value === mes)?.label + '/' + (ano || ''))
              ) : (
                <form autoComplete='off'>
                  <label>Competência</label>
                  <div className={styles.selects}>
                    <BrSelect
                      data-testid='select-mes-competencia'
                      options={listaMeses}
                      disabled={isEdicaoDadosRecolhimento}
                      placeholder={mes ? mesParaAbreviacao(mes) : 'Mês'}
                      className={styles.smallSelect}
                      value={mes}
                      onChange={(value: number) => setMes(value)}
                      key={mes}
                    />
                    /
                    <BrSelect
                      data-testid='select-ano-competencia'
                      options={listaAnos().map((ano) => ({ ...ano, label: String(ano.label) }))}
                      disabled={isEdicaoDadosRecolhimento}
                      placeholder={ano ? String(ano) : 'Ano'}
                      className={styles.smallSelect}
                      value={ano}
                      onChange={(value: number) => setAno(value)}
                      key={ano}
                    />
                  </div>
                </form>
              )}
              <Tooltip
                opacity={1}
                id='select-competencia-tooltip'
                place='bottom'
                className={styles.tooltip}
                variant='warning'
                disableTooltip={() => !isEdicaoDadosRecolhimento || isViewOnly}
              >
                <div className={styles.tooltipText}>
                  Não é possível editar a competência, por favor comece uma nova guia de recolhimento caso queira
                  alterá-la.
                </div>
              </Tooltip>
            </Col>
            <Col data-tooltip-id='select-processo-tooltip'>
              {isViewOnly || isEdicaoDadosRecolhimento ? (
                viewOnlyContent('Processo', formatarNrProcesso(dataDadosRecolhimento?.numeroProcesso || ''))
              ) : (
                <AnmSelectQuery
                  data-testid='select-processo'
                  label='Processo'
                  value={processo}
                  setValue={setProcesso}
                  isDisabled={(!ano || !mes) && !isEdicaoDadosRecolhimento}
                  customClassName={!ano && !mes ? styles.disabledSelectProcesso : styles.select}
                  functionQuery={() => getListaProcessoPorCpfCnpj(userCpfCnpj || '', mes || 0, ano || 0)}
                  keyQuery={['processoMinerarioQueryPorCpfCnpj', userCpfCnpj, mes, ano]}
                  formatResult={(result: IProcessoMinerarioETipoVinculo[]) =>
                    result.map((processoETipo) => ({
                      label: formatarNrProcesso(processoETipo.nrProcesso || ''),
                      value: processoETipo.nrProcesso,
                    }))
                  }
                  enableQuery={!!userCpfCnpj && !!mes && !!ano}
                  placeholder={formatarNrProcesso(processo) || 'Selecione o processo'}
                  setRetornoQuery={setListaProcessosData}
                />
              )}
              <Tooltip
                opacity={1}
                id='select-processo-tooltip'
                place='bottom'
                className={styles.tooltip}
                variant='warning'
                disableTooltip={() => (!!mes && !!ano && !isEdicaoDadosRecolhimento) || isViewOnly}
              >
                <div className={styles.tooltipText}>
                  {isEdicaoDadosRecolhimento
                    ? 'Não é possível editar o processo, por favor comece uma nova guia de recolhimento caso queira alterá-lo.'
                    : 'Selecione a competência antes de escolher o processo'}
                </div>
              </Tooltip>
            </Col>
            <Col data-tooltip-id='select-municipio-tooltip'>
              {isViewOnly || isEdicaoDadosRecolhimento ? (
                viewOnlyContent(
                  'Município/UF',
                  dataDadosRecolhimento?.municipio?.nome + '/' + (dataDadosRecolhimento?.municipio?.uf || '')
                )
              ) : (
                <AnmSelectQuery
                  data-testid='select-municipio'
                  label='Município/UF'
                  isDisabled={!processo && !isEdicaoDadosRecolhimento}
                  value={municipio}
                  setValue={setMunicipio}
                  customClassName={processo ? styles.select : styles.disabledSelect}
                  functionQuery={() => getProcessoMinerarioPorNumero(processo)}
                  keyQuery={['processoMinerarioQueryPorNumero', processo]}
                  formatResult={(result: IProcessoMinerario) =>
                    result.municipios.map((municipio: IMunicipio) => ({
                      label: municipio.nome + '/' + municipio.uf,
                      value: municipio.codigoIBGE,
                    }))
                  }
                  enableQuery={!!processo}
                  isPlaceHolderMunicipio
                />
              )}
              {
                <Tooltip
                  opacity={1}
                  id='select-municipio-tooltip'
                  place='bottom'
                  className={styles.tooltip}
                  variant='warning'
                  disableTooltip={() => (!!processo && !isEdicaoDadosRecolhimento) || isViewOnly}
                >
                  <div className={styles.tooltipText}>
                    {isEdicaoDadosRecolhimento
                      ? 'Não é possível editar o município, por favor comece uma nova guia de recolhimento caso queira alterá-lo.'
                      : 'Selecione o processo antes de escolher o município'}
                  </div>
                </Tooltip>
              }
            </Col>
          </Row>
          <Row>
            <Col data-tooltip-id='select-substancia-tooltip'>
              {isViewOnly || isEdicaoDadosRecolhimento ? (
                viewOnlyContent('Substância', dataDadosRecolhimento?.substancia || '')
              ) : (
                <AnmSelectQuery
                  data-testid='select-substancia'
                  label='Substância'
                  isDisabled={!processo && !isEdicaoDadosRecolhimento}
                  value={substancia}
                  setValue={setSubstancia}
                  customClassName={processo ? styles.select : styles.disabledSelect}
                  functionQuery={() => getProcessoMinerarioPorNumero(processo)}
                  keyQuery={['processoMinerarioQueryPorNumero', processo]}
                  formatResult={(result: IProcessoMinerario) =>
                    result.substancias.map((substancia: ISubstancia) => ({
                      label: substancia.nome,
                      value: substancia.nome,
                    }))
                  }
                  enableQuery={!!processo}
                  placeholder={substancia || 'Selecione a substância'}
                />
              )}
              <Tooltip
                opacity={1}
                id='select-substancia-tooltip'
                place='bottom'
                className={styles.tooltip}
                variant='warning'
                disableTooltip={() => (!!processo && !isEdicaoDadosRecolhimento) || isViewOnly}
              >
                <div className={styles.tooltipText}>
                  {isEdicaoDadosRecolhimento
                    ? 'Não é possível editar a substância, por favor comece uma nova guia de recolhimento caso queira alterá-la.'
                    : 'Selecione o processo antes de escolher a substância'}
                </div>
              </Tooltip>
            </Col>

            <Col data-tooltip-id='select-tipo-vinculo-tooltip'>
              {isEdicaoDadosRecolhimento || isViewOnly ? (
                viewOnlyContent(
                  'Tipo de Vínculo',
                  tipoVinculo === 1 ? 'Titular' : tipoVinculo === 2 ? 'Arrendatário' : ''
                )
              ) : (
                <>
                  <span className={styles.label}>Tipo de Vínculo</span>
                  <div className={styles.radioGroup}>
                    <BrRadioGroup
                      key={'radio-tipo-vinculo' + tipoVinculo}
                      data-testid='radio-tipo-vinculo'
                      name='radiogroup'
                      inline
                      options={[
                        {
                          id: '1',
                          label: 'Titular',
                          value: 1,
                        },
                        {
                          id: '2',
                          label: 'Arrendatário',
                          value: 2,
                        },
                      ]}
                      value={tipoVinculo}
                      onChange={(e) => setTipoVinculo(Number(e.target.value))}
                      disabled={tipoVinculo !== -1}
                    />
                  </div>
                </>
              )}
              <Tooltip
                opacity={1}
                id='select-tipo-vinculo-tooltip'
                place='bottom'
                className={styles.tooltip}
                variant='warning'
                disableTooltip={() => (!!processo && !isEdicaoDadosRecolhimento) || isViewOnly}
              >
                <div className={styles.tooltipText}>
                  {isEdicaoDadosRecolhimento
                    ? 'Não é possível editar o tipo de vínculo, por favor comece uma nova guia de recolhimento caso queira alterá-lo.'
                    : 'Selecione o processo para que o tipo do vínculo seja preenchido automaticamente'}
                </div>
              </Tooltip>
            </Col>
            <Col>
              {isViewOnly &&
                viewOnlyContent(
                  'Tipo de Recolhimento',
                  getValueFromETipoRecolhimentoByIndex(
                    dataDadosRecolhimento?.tipoRecolhimento === 0 || dataDadosRecolhimento?.tipoRecolhimento === 1
                      ? dataDadosRecolhimento?.tipoRecolhimento
                      : -1
                  ),
                  true
                )}
            </Col>
          </Row>
          <Row>
            <Col>
              {isViewOnly ? (
                viewOnlyContent('Observações', observacoes)
              ) : (
                <BrTextarea
                  data-testid='textarea-observacoes'
                  label='Observações'
                  value={observacoes}
                  onChange={(e) => setObservacoes(e.target.value)}
                />
              )}
            </Col>
          </Row>
        </>
      )}
    </div>
  );
};

export default DadosRecolhimento;
